import axios from "axios";
import { storageService } from "./storageService";
import { AppResponse } from "../models/common/app-response";
import { TokenDto } from "../models/auth/token.dto";
import { ROUTES } from "../constants/routes";


const loginApiUrl = "/api/v1/accounts/login";
const refreshTokenApiUrl = "/api/v1/accounts/refresh-token";
let refreshRequest: Promise<TokenDto> | null = null;

const instance = axios.create({
    baseURL: window.__env.apiUrl,
    headers: {
        "Content-Type": "application/json",
    },
});

const logout = () => {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    storageService.clearStorage();
    window.location.href = `${ROUTES.auth.login}?returnUrl=${returnUrl}`;
};

const refreshAccessToken = async (): Promise<TokenDto> => {
    const refreshToken = storageService.getTokenData()?.refreshToken;
    if (!refreshToken) {
        throw new Error("Refresh token is missing");
    }

    if (!refreshRequest) {
        refreshRequest = instance
            .post<AppResponse<TokenDto>>(refreshTokenApiUrl, { refreshToken })
            .then((response) => {
                if (!response.data.success || !response.data.data) {
                    throw new Error(response.data.message || "Failed to refresh token");
                }

                storageService.setTokenData(response.data.data);
                return response.data.data;
            })
            .finally(() => {
                refreshRequest = null;
            });
    }

    return refreshRequest;
};

instance.interceptors.request.use(
    (config) => {
        const token = storageService.getTokenData()?.accessToken;
        if (token) {
            config.headers["Authorization"] = 'Bearer ' + token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

instance.interceptors.response.use(
    (res) => {
        return res;
    },
    async (err) => {

        const originalConfig = err.config;

        if (err.response && err.response.status === 401 && originalConfig.url !== loginApiUrl) {
            if (originalConfig.url === refreshTokenApiUrl) {
                logout();
                return Promise.reject(err);
            }

            if (originalConfig._retry) {
                logout();
                return Promise.reject(err);
            }

            originalConfig._retry = true;
            try {
                await refreshAccessToken();
                return instance(originalConfig);
            } catch (refreshError) {
                logout();
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(err);
    }
);

export default instance;
